import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { GridRowId } from '@mui/x-data-grid-pro'
import { PackageDetail } from '../../_common/interfaces'
import { Dayjs } from 'dayjs'
import { mercuryApi } from '$api'
import useAppStore from '$appStore'
import {
  exceptionMessageHandler,
  PermissionType,
  readStorageKey,
  StorageScope,
  writeStorageKey,
} from '$utils'
import { IClockinResponse, IPaySummaryReportResponse } from '$models'
import { paySummaryService } from '$services'
import {
  getManuallyToggledIds,
  haveSameIdsInOrder,
  toggleId,
} from '$components/common'
import { saveClockin } from '../../_common/helpers'

/** Storage key for the expand all choice, per user and company */
export const EXPAND_ALL_PREFERENCE = 'pay-summary/expand-all'

export type PaySummarySection = 'role' | 'adjustment' | 'leave'

/** In the store so a toggle re-renders only what reads it */
interface PaySummaryExpansionState {
  /** The header toggle's choice. Sections and rows start from it. */
  isAllExpanded: boolean
  openSections: Record<PaySummarySection, boolean>
  /** Rows the user flipped away from the expand all choice */
  manuallyToggledStaffIds: GridRowId[]
  manuallyToggledRoleIds: GridRowId[]
}

interface PaySummaryState extends PaySummaryExpansionState {
  paySummaryReport?: IPaySummaryReportResponse
  allPackages: PackageDetail[]
}

export interface PaySummarySlice extends PaySummaryState {
  resetPaySummarySlice: () => void

  /** Back to the remembered expand all choice, nothing toggled by hand */
  resetExpansion: () => void
  /** Also remembers the choice per user and company */
  setAllExpanded: (isExpanded: boolean) => void
  setSectionOpen: (section: PaySummarySection, isOpen: boolean) => void
  toggleStaffRow: (rowId: GridRowId) => void
  /** Turns what the grid reports as open back into manual toggles */
  setExpandedStaffRows: (
    rowIds: readonly GridRowId[],
    expandedIds: readonly GridRowId[]
  ) => void
  toggleRoleRow: (rowId: GridRowId) => void
  setExpandedRoleRows: (
    rowIds: readonly GridRowId[],
    expandedIds: readonly GridRowId[]
  ) => void

  getPaySummaryReport: (
    startDateTime: Dayjs,
    endDateTime: Dayjs
  ) => Promise<void>

  getPackages: () => Promise<void>

  /**
   * Saves an edited shift and reloads the report, so the totals follow the change
   * @returns true when the save went through
   */
  updateClockin: (
    clockin: IClockinResponse,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>
}

/** Stored per user and company. Null until both are known. */
const getPreferenceScope = (): StorageScope | null => {
  const { activeCompany, userDetails } = useAppStore.getState()
  const companyId = activeCompany?.id
  const userId = userDetails.id

  return companyId && userId ? { companyId, userId } : null
}

const buildExpansionState = (
  isAllExpanded: boolean
): PaySummaryExpansionState => ({
  isAllExpanded,
  openSections: {
    role: isAllExpanded,
    adjustment: isAllExpanded,
    leave: isAllExpanded,
  },
  manuallyToggledStaffIds: [],
  manuallyToggledRoleIds: [],
})

const initialState: PaySummaryState = {
  paySummaryReport: undefined,
  allPackages: [],
  ...buildExpansionState(false),
}

/** The period the report currently shows, so an edited shift can reload the same one */
const getReportDateRange = (): [Dayjs, Dayjs] => {
  const { startDateTime, endDateTime } = useAppStore.getState().reportsMetadata
  return [startDateTime, endDateTime]
}

const createPaySummarySlice: StateCreator<
  PaySummarySlice & SharedSlice,
  [],
  [],
  PaySummarySlice
> = (set, get) => ({
  ...initialState,

  resetPaySummarySlice: () => set(initialState),

  resetExpansion: () => {
    const scope = getPreferenceScope()
    set(
      buildExpansionState(
        scope ? readStorageKey(EXPAND_ALL_PREFERENCE, scope, false) : false
      )
    )
  },

  setAllExpanded: (isExpanded) => {
    const scope = getPreferenceScope()
    if (scope) writeStorageKey(EXPAND_ALL_PREFERENCE, scope, isExpanded)
    set(buildExpansionState(isExpanded))
  },

  setSectionOpen: (section, isOpen) =>
    set((state) => ({
      openSections: { ...state.openSections, [section]: isOpen },
    })),

  toggleStaffRow: (rowId) =>
    set((state) => ({
      manuallyToggledStaffIds: toggleId(state.manuallyToggledStaffIds, rowId),
    })),

  // Hand back the same state when nothing changed, so no subscriber re-renders and the grid
  // does not report it again in a loop
  setExpandedStaffRows: (rowIds, expandedIds) =>
    set((state) => {
      const nextManuallyToggledIds = getManuallyToggledIds(
        rowIds,
        expandedIds,
        state.isAllExpanded
      )
      return haveSameIdsInOrder(
        state.manuallyToggledStaffIds,
        nextManuallyToggledIds
      )
        ? state
        : { manuallyToggledStaffIds: nextManuallyToggledIds }
    }),

  toggleRoleRow: (rowId) =>
    set((state) => ({
      manuallyToggledRoleIds: toggleId(state.manuallyToggledRoleIds, rowId),
    })),

  setExpandedRoleRows: (rowIds, expandedIds) =>
    set((state) => {
      const nextManuallyToggledIds = getManuallyToggledIds(
        rowIds,
        expandedIds,
        state.isAllExpanded
      )
      return haveSameIdsInOrder(
        state.manuallyToggledRoleIds,
        nextManuallyToggledIds
      )
        ? state
        : { manuallyToggledRoleIds: nextManuallyToggledIds }
    }),

  getPaySummaryReport: async (
    startDateTime: Dayjs,
    endDateTime: Dayjs
  ): Promise<void> => {
    return paySummaryService
      .getReport(startDateTime, endDateTime)
      .then((report) => set(() => ({ paySummaryReport: report })))
      .catch((error) => {
        set(() => ({ paySummaryReport: undefined }))
        exceptionMessageHandler(error, 'Failed to retrieve the pay summary')
      })
  },

  getPackages: async (): Promise<void> => {
    const { activeCompany } = useAppStore.getState()

    const hasPayrollPermission = activeCompany?.hasPermission(
      PermissionType.Payroll
    )

    if (!hasPayrollPermission) {
      return set(() => ({ allPackages: [] }))
    }

    return mercuryApi
      .get<PackageDetail[]>(`/pay-rules/packages`)
      .then((result) => set(() => ({ allPackages: result })))
      .catch((error) => {
        set(() => ({ allPackages: [] }))
        exceptionMessageHandler(error, 'Failed to retrieve packages')
      })
  },

  updateClockin: async (
    clockin: IClockinResponse,
    successMessage: string = 'The shift has been updated',
    errorMessage: string = 'Failed to update the shift'
  ): Promise<boolean> =>
    saveClockin({
      clockin,
      getClockins: () =>
        (get().paySummaryReport?.staff ?? []).flatMap(
          (staff) => staff.clockins
        ),
      refetchAfterSave: () =>
        get().getPaySummaryReport(...getReportDateRange()),
      setLoading: get().setIsLoading,
      successMessage,
      errorMessage,
    }),
})

export default createPaySummarySlice
